import * as React from "react";
import { Dispatch, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  InputBase,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  ButtonGroup,
  StandardTextFieldProps,
  styled,
  withWidth,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import {
  FeatureWithName,
  useFeatureSuggestions,
} from "./useFeatureSuggestions";
import { featureToLines, getQualifierByRelation } from "fe-common/logic";
import { GenderValue, Relations, Values } from "shared";
import { QualifiedFeatureValue } from "fe-common/graphql";
import { groupQualifiedWithLevel } from "./groupFeaturesWithLevel";
import { CommonColors } from "../colors/commonColors";

//region [[ Styles ]]
const InputField = styled(InputBase)(({ theme }) => ({
  width: "100%",
  fontWeight: 500,
  [theme.breakpoints.down("xs")]: {
    minWidth: "auto",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "black",
  },
  color: "black",
  padding: theme.spacing(2),
  fontFamily: "Poppins",
  "& input[type='text']:focus, & input[type='email']:focus, & input[type='url']:focus, & input[type='password']:focus, & input[type='search']:focus, & input[type='number']:focus, & input[type='tel']:focus, & input[type='range']:focus, & input[type='date']:focus & input[type='month']:focus, & input[type='week']:focus, & input[type='time']:focus, & input[type='datetime']:focus, & input[type='datetime-local']:focus, & input[type='color']:focus, & textarea:focus": {
    color: KahunColorMap.BlackPatientFacing,
  },
  "&.disabled input[type='text']:focus, &.disabled input[type='email']:focus, &.disabled input[type='url']:focus, &.disabled input[type='password']:focus, &.disabled input[type='search']:focus, &.disabled input[type='number']:focus, &.disabled input[type='tel']:focus, &.disabled input[type='range']:focus, &.disabled input[type='date']:focus &.disabled input[type='month']:focus, &.disabled input[type='week']:focus, &.disabled input[type='time']:focus, & input[type='datetime']:focus, &.disabled input[type='datetime-local']:focus, &.disabled input[type='color']:focus, &.disabled textarea:focus": {
    // color: CommonColors.DefaultLight,
    color: "gray",
  },
  "& input, & input[type='text'], & input[type='email'], & input[type='url'], & input[type='password'], & input[type='search'], & input[type='number'], & input[type='tel'], & input[type='range'], & input[type='date'], & input[type='month'], & input[type='week'], & input[type='time'], & input[type='datetime'], & input[type='datetime-local'], & input[type='color']": {
    background: "transparent",
    border: "none",
    height: "auto",
    margin: 0,
    color: KahunColorMap.BlackPatientFacing,
    padding: "6px 0 7px",
  },
  ".disabled & input, .disabled & input[type='text'], .disabled & input[type='email'],.disabled & input[type='url'],.disabled & input[type='password'],.disabled & input[type='search'],.disabled & input[type='number'],.disabled & input[type='tel'],.disabled & input[type='range'],.disabled & input[type='date'],.disabled & input[type='month'],.disabled & input[type='week'],.disabled & input[type='time'],.disabled & input[type='datetime'],.disabled & input[type='datetime-local'],.disabled & input[type='color']": {
    // color: CommonColors.DefaultDark,
    color: "gray",
  },
}));

const SearchBox = styled(Box)(({ theme }) => ({
  width: "auto", // was hardcoded 500px - not sure why TBD JM
  flex: 1,
  [theme.breakpoints.down("xs")]: {
    width: "auto",
  },
  position: "relative",
}));

const StyledSearchIcon = styled(SearchIcon)(({ theme }) => ({
  color: KahunColorMap.Secondary,
  marginRight: "5px",
  marginLeft: "5px",
  [theme.breakpoints.up("xs")]: {
    "font-size": "30px",
  },
  [theme.breakpoints.down("xs")]: {
    "font-size": "larger",
  },
}));

const AutoCompleteList = styled(List)(() => ({
  "background-color": "white",
  height: "auto",
  "max-height": "400px",
  width: "100%",
  overflow: "auto",
  "z-index": 3,
  "box-shadow": "rgb(239, 239, 239) 0px 2px 8px 6px",
}));

const InputContainer = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
}));

const CancelButton = styled("div")(({ theme }) => ({
  fontSize: "14px",
  cursor: "pointer",
  position: "absolute",
  top: "6px",
  padding: "3px",
  right: "10px",
  color: "#376B74",
  backgroundColor: "#e7edee",
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface SearchOptions {
  patientFacing?: boolean;
  panelIds?: string[];
  ancestorsIds?: string[];
  gender?: GenderValue;
  categories?: string[];
}

export interface AkAutoCompleteProps
  extends Omit<StandardTextFieldProps, "onChange"> {
  ResultsComponent?: React.ComponentType<React.HTMLAttributes<HTMLElement>>;
  onChange?: (
    event: React.ChangeEvent<{}>,
    value: FeatureWithName | null,
  ) => void;
  onLoading?: (boolean) => void;
  onSuggestions?: (suggestions: FeatureWithName[]) => any;
  initialValue?: string;
  selectedValue?: string;
  onClick?: () => any;
  hideResults?: boolean;
  showCancel?: boolean;
  searchIcon?: any;
  disabled?: boolean;
  icon?: any;
  searchOptions?: SearchOptions;
  onOpen?: () => void; // Called when the result box should be open
  onClose?: (event: React.MouseEvent<Document>) => void; // Called when the result box is closed
  hideIcon?: boolean;
  term?: string;
  onTermChanged?: (string) => void;
  // a hack if set to true will set patient facing = true prop in each feature
  // to get the translations
  byPassPatientFacing?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]

export const AkFeatureSelection = React.forwardRef(
  (
    {
      searchOptions,
      onOpen,
      onClose,
      onLoading,
      ...props
    }: AkAutoCompleteProps,
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [term, setTerm] = React.useState<string>(props.initialValue);

    const ResultComponent = props.ResultsComponent
      ? props.ResultsComponent
      : AutoCompleteList;

    const { loading, results } = useFeatureSuggestions(
      term,
      searchOptions.categories,
      searchOptions.gender,
      searchOptions.patientFacing,
      searchOptions.panelIds,
      searchOptions.ancestorsIds,
    );

    const groupedOptions = useMemo(() => groupQualifiedWithLevel(results), [
      results,
    ]);

    useEffect(() => {
      onLoading?.(loading);
    }, [loading]);

    useEffect(() => {
      if (groupedOptions && props.onSuggestions) {
        props.onSuggestions(groupedOptions);
      }

      if (props.selectedValue) {
        setTerm(null);
      }
    }, [groupedOptions, props.onSuggestions, props.selectedValue]);

    const handleFieldChanged = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // If we're controlled let the parent change the term
        if (props.onTermChanged) {
          props.onTermChanged(e.target.value || "");
          return;
        }
        setTerm(e.target.value || "");
      },
      [props.onTermChanged, setTerm],
    );

    const { onFocus, onBlur } = props;

    const handleFocus = React.useCallback(
      (e) => {
        onFocus && onFocus(e);
        setOpen(true);
        onOpen?.();
      },
      [onFocus, setOpen, onOpen],
    );

    const handleBlur = React.useCallback(
      (e) => {
        onBlur && onBlur(e);
        setOpen(false);
        onClose?.(e);
      },
      [onBlur, setOpen, onClose],
    );

    useEffect(() => {
      setTerm(props.term);
    }, [props.term]);

    const handleCancel = () => {
      setTerm("");
    };

    return (
      <ClickAwayListener onClickAway={handleBlur}>
        <SearchBox className={props.className}>
          <InputContainer>
            <InputField
              className={"ak-feature-selection-input"}
              autoFocus={props.autoFocus}
              onFocus={handleFocus}
              startAdornment={
                props.hideIcon ? null : props.searchIcon ? (
                  props.searchIcon
                ) : (
                  <StyledSearchIcon />
                )
              }
              style={{ flex: 1 }}
              endAdornment={
                loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  <>{props.icon}</>
                )
              }
              onChange={handleFieldChanged}
              placeholder={
                props.placeholder
                  ? props.placeholder
                  : "Search clinical finding correlation to COVID-19. e.g. Fever, Cough"
              }
              value={term || term === "" ? term : props.selectedValue || ""}
              ref={ref}
              disabled={props.disabled}
              onClick={() => {
                if (!props.disabled) {
                  setTerm("");
                  props?.onClick?.();
                }
              }}
            />
            {props.showCancel === true &&
              groupedOptions.length > 0 &&
              !loading && (
                <CancelButton onClick={handleCancel}>Cancel</CancelButton>
              )}
          </InputContainer>
          {open && groupedOptions.length > 0 && !props.hideResults && (
            <ResultComponent className={"ak-feature-selection-results"}>
              {groupedOptions.map((suggestion, index) => {
                const isLevel = getLevelQualifier(suggestion) != null;
                const itemProps = {
                  suggestion,
                  onClick: (s) => {
                    props.onChange(null, s);
                    onClose();
                    setTerm(null);
                    setOpen(false);
                  },
                };
                return isLevel ? (
                  <LevelItem
                    {...itemProps}
                    patientFacing={
                      searchOptions.patientFacing || props.byPassPatientFacing
                    }
                  />
                ) : (
                  <YesNoItem
                    {...itemProps}
                    patientFacing={
                      searchOptions.patientFacing || props.byPassPatientFacing
                    }
                  />
                );
              })}
            </ResultComponent>
          )}
        </SearchBox>
      </ClickAwayListener>
    );
  },
);

interface ListItemProps {
  suggestion: FeatureWithName;
  onClick: Dispatch<FeatureWithName>;
  patientFacing?: boolean;
}

function YesNoItem({ suggestion, onClick, patientFacing }: ListItemProps) {
  const text = featureToLines(
    suggestion.value,
    false,
    null,
    null,
    patientFacing,
  );
  return (
    <ListItem
      style={{ marginBottom: "-11px" }}
      button
      key={suggestion.value.id}
      onClick={() => onClick(suggestion)}
    >
      <ListItemText primary={text[0]} secondary={text[1]} />
    </ListItem>
  );
}

function LevelItem({ suggestion, onClick, patientFacing }: ListItemProps) {
  let q = getLevelQualifier(suggestion);
  const text = featureToLines(
    {
      ...suggestion.value,
      mandatory: null,
      qualifiers: [],
    },
    false,
    null,
    null,
    patientFacing,
  );

  const dispatchWithLevel = (level) => () =>
    onClick({
      name: suggestion.name,
      value: {
        ...suggestion.value,
        qualifiers: [{ ...q, value: level }],
        mandatory: { relation: q.relation, value: level, range: null },
      },
    });

  return (
    <ListItem
      style={{ marginBottom: "-11px" }}
      button
      key={suggestion.value.id}
    >
      <ListItemText primary={text[0]} secondary={text[1]} />
      <ListItemSecondaryAction>
        <ButtonGroup variant="text" color="primary">
          <Button
            size={"small"}
            onClick={dispatchWithLevel(LevelRelations.low)}
          >
            Low
          </Button>
          <Button
            size={"small"}
            onClick={dispatchWithLevel(LevelRelations.normal)}
          >
            Norm
          </Button>
          <Button
            size={"small"}
            onClick={dispatchWithLevel(LevelRelations.high)}
          >
            High
          </Button>
        </ButtonGroup>
      </ListItemSecondaryAction>
    </ListItem>
  );
}

function getLevelQualifier(s: FeatureWithName) {
  return getQualifierByRelation(s.value, Relations.LEVEL);
}

const LevelRelations: Record<string, QualifiedFeatureValue> = {
  low: { name: "Low", shortname: "Low", id: Values.LOW, category: null },
  high: { name: "High", shortname: "High", id: Values.HIGH, category: null },
  normal: {
    name: "Normal",
    shortname: "Normal",
    id: Values.NORMAL,
    category: null,
  },
};
